import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  isBlogLevel,
  LEVEL_LABELS,
  LEVELS,
} from '../../../blog/blog-categories';
import { BLOG_POSTS } from '../../../blog/blog-posts-token';
import { isHubIndexable } from '../../../blog/hub-indexing';
import { PageCtaComponent } from '../../../content/page-cta/page-cta.component';
import { BRAND_SUFFIX, PageTitleService } from '../../../page-title';
import { RouteUiDataService } from '../../../route-ui-data';
import { SEOService } from '../../../seo.service';
import { BlogPostCardComponent } from '../blog-post-card/blog-post-card.component';

@Component({
  selector: 'app-blog-level',
  imports: [RouterLink, BlogPostCardComponent, PageCtaComponent],
  templateUrl: './blog-level.component.html',
  styleUrl: './blog-level.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlogLevelComponent {
  private pageTitle = inject(PageTitleService);
  private seo = inject(SEOService);
  private routeUiData = inject(RouteUiDataService);
  private allPosts = inject(BLOG_POSTS);

  readonly level = input.required<string>();

  readonly isValid = computed(() => isBlogLevel(this.level()));

  readonly label = computed(() => LEVEL_LABELS[this.level()] ?? this.level());

  readonly posts = computed(() =>
    this.isValid() ? this.allPosts.filter((p) => p.level === this.level()) : [],
  );

  constructor() {
    effect(() => {
      if (!isHubIndexable(this.posts().length)) {
        this.seo.setRobotsNoindex();
      } else {
        this.seo.clearRobots();
      }

      if (this.isValid()) {
        const descriptor = LEVELS.find((l) => l.slug === this.level());
        const heading = this.label();
        const fullTitle = `${heading} | Bass Guitar Blog${BRAND_SUFFIX}`;
        this.pageTitle.setPageTitle(fullTitle, {
          title: heading,
          ...(descriptor ? { lead: descriptor.description } : {}),
        });
        this.routeUiData.setBreadcrumbOverride([
          { name: 'Home', path: '/' },
          { name: 'Blog', path: '/blog' },
          { name: heading },
        ]);
      } else {
        const fullTitle = `Level not found | Bass Guitar Blog${BRAND_SUFFIX}`;
        this.pageTitle.setPageTitle(fullTitle, { title: 'Level not found' });
        this.routeUiData.setBreadcrumbOverride([
          { name: 'Home', path: '/' },
          { name: 'Blog', path: '/blog' },
          { name: 'Level not found' },
        ]);
      }
    });
  }
}
